import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { Lock, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).filter(Boolean).join(' ');
  return 'Something went wrong. Please try again.';
};

export default function ChangePasswordPage() {
  const [form, setForm] = useState({ memberId: '', current: '', next: '', confirm: '' });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    const memberId = form.memberId.trim();
    if (!/^\d{10}$/.test(memberId)) return toast.error('Please enter your 10-digit Member ID');
    if (form.next.length < 6) return toast.error('New password must be at least 6 characters');
    if (form.next !== form.confirm) return toast.error('New passwords do not match');
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { identifier: memberId, password: form.current });
      if (data.user?.role !== 'member') {
        toast.error('This page is for members only.');
        return;
      }
      await axios.post(
        `${API}/auth/change-password`,
        { current_password: form.current, new_password: form.next },
        { headers: { Authorization: `Bearer ${data.access_token}` } }
      );
      toast.success('Password changed. Please sign in with your new password.');
      navigate('/member/login');
    } catch (err) {
      const msg = errText(err);
      toast.error(msg === 'Invalid credentials' ? 'Member ID or current password is incorrect' : msg);
    } finally {
      setLoading(false);
    }
  };

  const field = (k, label, props = {}) => (
    <div>
      <label className="input-label">{label}</label>
      <input value={form[k]} onChange={set(k)} className="input-gold" data-testid={`change-pwd-${k}-input`} {...props} />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center p-4" data-testid="change-password-page">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md glass-gold p-8">
        <h1 className="text-2xl font-bold text-white mb-2 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
          <Lock className="w-6 h-6 text-[#D4AF37]" /> Change Password
        </h1>
        <p className="text-gray-400 text-sm mb-6">
          Enter your Member ID and current password. If you never changed it, your current password is your Member ID.
        </p>
        <form onSubmit={submit} className="space-y-5">
          {field('memberId', 'Member ID', { inputMode: 'numeric', maxLength: 10, placeholder: '10-digit Member ID' })}
          {field('current', 'Current Password', { type: 'password' })}
          {field('next', 'New Password', { type: 'password' })}
          {field('confirm', 'Confirm New Password', { type: 'password' })}
          <button type="submit" disabled={loading} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="change-pwd-submit-btn">
            {loading && <Loader2 className="w-4 h-4 animate-spin" />} Change Password
          </button>
        </form>
        <p className="mt-6 text-center text-sm">
          <Link to="/member/login" className="text-[#D4AF37] hover:underline" data-testid="change-pwd-back-to-login">Back to Login</Link>
        </p>
      </motion.div>
    </div>
  );
}
